import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { AutomationFiltersTypesMap } from './filtersTypes';

interface BaseFilterValues {
  [filterKey: string]: {
    selectedValues: string[];
    isAllSelected: boolean;
  };
}

interface AutomationFiltersContextValue {
  filterValues: BaseFilterValues;
  setFilterValue: (filterKey: string, selectedValues: string[]) => void;
  clearAllFilterValues: () => void;
}

interface AutomationFiltersProviderProps {
  children: ReactNode;
}

const AutomationFiltersContext = createContext<AutomationFiltersContextValue | undefined>(
  undefined,
);

/**
 * Provider that manages automation filter values state only
 * Filter definitions are now passed via props to components
 */
export const AutomationFiltersProvider: React.FC<AutomationFiltersProviderProps> = ({
  children,
}) => {
  // Filter values state management only
  const [filterValues, setFilterValues] = useState<BaseFilterValues>({});

  const setFilterValue = useCallback((filterKey: string, selectedValues: string[]) => {
    const clearRecordTypesFilterIfObjectChanged: BaseFilterValues =
      filterKey === AutomationFiltersTypesMap.object
        ? {
            [AutomationFiltersTypesMap.recordTypes]: {
              selectedValues: [],
              isAllSelected: true,
            },
          }
        : {};

    const clearStepFilterIfRecordTypesChanged: BaseFilterValues =
      filterKey === AutomationFiltersTypesMap.recordTypes ||
      filterKey === AutomationFiltersTypesMap.object
        ? {
            [AutomationFiltersTypesMap.steps]: {
              selectedValues: [],
              isAllSelected: true,
            },
          }
        : {};

    setFilterValues((prev) => ({
      ...prev,
      ...clearStepFilterIfRecordTypesChanged,
      ...clearRecordTypesFilterIfObjectChanged,
      [filterKey]: {
        selectedValues,
        isAllSelected: selectedValues.length === 0, // Consider empty selection as "all selected"
      },
    }));
  }, []);

  const clearAllFilterValues = useCallback(() => {
    setFilterValues({});
  }, []);

  const value: AutomationFiltersContextValue = useMemo(
    () => ({
      filterValues,
      setFilterValue,
      clearAllFilterValues,
    }),
    [filterValues, setFilterValue, clearAllFilterValues],
  );

  return (
    <AutomationFiltersContext.Provider value={value}>{children}</AutomationFiltersContext.Provider>
  );
};

/**
 * Hook to access AutomationFilters context (only for filter values state)
 */
export const useAutomationFiltersContext = () => {
  const context = useContext(AutomationFiltersContext);
  if (context === undefined) {
    throw new Error('useAutomationFiltersContext must be used within a AutomationFiltersProvider');
  }
  return context;
};
